import { defineComponent as _defineComponent } from 'vue';
import { faExclamation, faSquare, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BAlert, BBadge, BButton, BTab, BTabs } from "bootstrap-vue";
import { computed, onUnmounted, ref, watch } from "vue";
import {} from "@/api/invocations";
import { useAnimationFrameResizeObserver } from "@/composables/sensors/animationFrameResizeObserver";
import { useInvocationStore } from "@/stores/invocationStore";
import { useWorkflowStore } from "@/stores/workflowStore";
import { errorMessageAsString } from "@/utils/simple-error";
import { errorCount as jobStatesSummaryErrorCount, isTerminal, jobCount as jobStatesSummaryJobCount, numTerminal, okCount as jobStatesSummaryOkCount, runningCount as jobStatesSummaryRunningCount, } from "./util";
import ProgressBar from "../ProgressBar.vue";
import WorkflowInvocationSteps from "../Workflow/Invocation/Graph/WorkflowInvocationSteps.vue";
import InvocationReport from "../Workflow/InvocationReport.vue";
import WorkflowAnnotation from "../Workflow/WorkflowAnnotation.vue";
import WorkflowNavigationTitle from "../Workflow/WorkflowNavigationTitle.vue";
import WorkflowInvocationExportOptions from "./WorkflowInvocationExportOptions.vue";
import WorkflowInvocationInputOutputTabs from "./WorkflowInvocationInputOutputTabs.vue";
import WorkflowInvocationMetrics from "./WorkflowInvocationMetrics.vue";
import WorkflowInvocationOverview from "./WorkflowInvocationOverview.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'WorkflowInvocationState',
    props: {
        invocationId: null,
        isSubworkflow: { type: Boolean, default: false },
        isFullPage: { type: Boolean },
        success: { type: Boolean }
    },
    emits: ["invocation-cancelled"],
    setup(__props, { emit }) {
        const props = __props;
        const invocationStore = useInvocationStore();
        const stepStatesInterval = ref(undefined);
        const jobStatesInterval = ref(undefined);
        const invocationLoaded = ref(false);
        const errorMessage = ref(null);
        const cancellingInvocation = ref(false);
        // after the report tab is first activated, no longer lazy-render it from then on
        const reportActive = ref(false);
        const reportLazy = ref(true);
        watch(() => reportActive.value, (newValue) => {
            if (newValue) {
                reportLazy.value = false;
            }
        });
        const workflowStore = useWorkflowStore();
        const tabsDisabled = computed(() => !invocationStateSuccess.value ||
            !invocation.value ||
            !workflowStore.getStoredWorkflowByInstanceId(invocation.value.workflow_id));
        /** Tooltip message for the a tab when it is disabled */
        const disabledTabTooltip = computed(() => {
            const state = invocationState.value;
            if (state != "scheduled") {
                return `This workflow is not currently scheduled. The current state is ${state}. Once the workflow is fully scheduled and jobs have complete any disabled tabs will become available.`;
            }
            else if (stateCounts.value && stateCounts.value.runningCount != 0) {
                return `The workflow invocation still contains ${stateCounts.value.runningCount} running job(s). Once these jobs have completed any disabled tabs will become available.`;
            }
            else {
                return "Steps for this workflow are still running. Any disabled tabs will be available once complete.";
            }
        });
        const invocationTabs = ref();
        const scrollableDiv = computed(() => invocationTabs.value?.$el.querySelector(".tab-content"));
        const isScrollable = ref(false);
        useAnimationFrameResizeObserver(scrollableDiv, ({ clientSize, scrollSize }) => {
            isScrollable.value = scrollSize.height >= clientSize.height + 1;
        });
        const invocation = computed(() => invocationLoaded.value
            ? invocationStore.getInvocationById(props.invocationId)
            : null);
        const invocationState = computed(() => invocation.value?.state || "new");
        const invocationAndJobTerminal = computed(() => invocationSchedulingTerminal.value && jobStatesTerminal.value);
        const invocationSchedulingTerminal = computed(() => {
            return (invocationState.value == "scheduled" ||
                invocationState.value == "cancelled" ||
                invocationState.value == "failed");
        });
        const jobStatesTerminal = computed(() => {
            // If the job states summary is null, we haven't fetched it yet
            // If the `populated_state` for the summary is `new`, we haven't finished scheduling all jobs
            if (jobStatesSummary.value === null || jobStatesSummary.value.populated_state === "new") {
                return false;
            }
            if (invocationSchedulingTerminal.value && jobCount.value === 0) {
                // no jobs for this invocation (think it has just subworkflows/inputs)
                return true;
            }
            return isTerminal(jobStatesSummary.value);
        });
        const jobStatesSummary = computed(() => invocationStore.getInvocationJobsSummaryById(props.invocationId));
        const invocationStateSuccess = computed(() => {
            return (invocationState.value == "scheduled" && stateCounts.value?.runningCount === 0 && invocationAndJobTerminal.value);
        });
        const stepStates = computed(() => {
            const stepStates = {};
            const steps = invocation.value?.steps || [];
            for (const step of steps) {
                if (!step) {
                    continue;
                }
                // the API defined state here allowing null and undefined is odd...
                const stepState = step.state || "unknown";
                if (!stepStates[stepState]) {
                    stepStates[stepState] = 1;
                }
                else {
                    stepStates[stepState] += 1;
                }
            }
            return stepStates;
        });
        const stepCount = computed(() => {
            return invocation.value?.steps.length || 0;
        });
        const stepStatesStr = computed(() => {
            return `${stepStates.value?.scheduled || 0} of ${stepCount.value} steps successfully scheduled.`;
        });
        const stateCounts = computed(() => {
            if (jobStatesSummary.value === null) {
                return null;
            }
            const okCount = jobStatesSummaryOkCount(jobStatesSummary.value);
            const errorCount = jobStatesSummaryErrorCount(jobStatesSummary.value);
            const runningCount = jobStatesSummaryRunningCount(jobStatesSummary.value);
            const newCount = jobCount.value - okCount - runningCount - errorCount;
            return { okCount, errorCount, runningCount, newCount };
        });
        const jobCount = computed(() => {
            return jobStatesSummaryJobCount(jobStatesSummary.value);
        });
        const jobStatesStr = computed(() => {
            if (jobStatesSummary.value === null) {
                return "No jobs summary available yet.";
            }
            let jobStr = `${numTerminal(jobStatesSummary.value) || 0} of ${jobCount.value} jobs complete`;
            if (!invocationSchedulingTerminal.value) {
                jobStr += " (total number of jobs will change until all steps fully scheduled)";
            }
            return `${jobStr}.`;
        });
        watch(() => props.invocationId, async (id) => {
            invocationLoaded.value = false;
            try {
                await invocationStore.fetchInvocationForId({ id });
                invocationLoaded.value = true;
                // Only start polling if there is a valid invocation
                if (invocation.value) {
                    await pollStepStatesUntilTerminal();
                    await pollJobStatesUntilTerminal();
                }
            }
            catch (e) {
                errorMessage.value = errorMessageAsString(e);
            }
        }, { immediate: true });
        const storeId = computed(() => (invocation.value ? `invocation-${invocation.value.id}` : undefined));
        watch(() => invocationSchedulingTerminal.value, async (newVal, oldVal) => {
            if (oldVal && !newVal) {
                // If the invocation was terminal and now is not, start polling again
                await pollStepStatesUntilTerminal();
            }
        });
        onUnmounted(() => {
            clearTimeout(stepStatesInterval.value);
            clearTimeout(jobStatesInterval.value);
        });
        async function pollStepStatesUntilTerminal() {
            if (!invocationSchedulingTerminal.value) {
                await invocationStore.fetchInvocationForId({ id: props.invocationId });
                stepStatesInterval.value = setTimeout(pollStepStatesUntilTerminal, 3000);
            }
        }
        async function pollJobStatesUntilTerminal() {
            if (!jobStatesTerminal.value) {
                await invocationStore.fetchInvocationJobsSummaryForId({ id: props.invocationId });
                jobStatesInterval.value = setTimeout(pollJobStatesUntilTerminal, 3000);
            }
        }
        function onError(e) {
            console.error(e);
        }
        async function onCancel() {
            try {
                cancellingInvocation.value = true;
                await invocationStore.cancelWorkflowScheduling(props.invocationId);
            }
            catch (e) {
                onError(e);
            }
            finally {
                emit("invocation-cancelled");
                cancellingInvocation.value = false;
            }
        }
        return { __sfc: true, props, emit, invocationStore, stepStatesInterval, jobStatesInterval, invocationLoaded, errorMessage, cancellingInvocation, reportActive, reportLazy, workflowStore, tabsDisabled, disabledTabTooltip, invocationTabs, scrollableDiv, isScrollable, invocation, invocationState, invocationAndJobTerminal, invocationSchedulingTerminal, jobStatesTerminal, jobStatesSummary, invocationStateSuccess, stepStates, stepCount, stepStatesStr, stateCounts, jobCount, jobStatesStr, storeId, pollStepStatesUntilTerminal, pollJobStatesUntilTerminal, onError, onCancel, faExclamation, faSquare, faTimes, FontAwesomeIcon, BAlert, BBadge, BButton, BTab, BTabs, ProgressBar, WorkflowInvocationSteps, InvocationReport, WorkflowAnnotation, WorkflowNavigationTitle, WorkflowInvocationExportOptions, WorkflowInvocationInputOutputTabs, WorkflowInvocationMetrics, WorkflowInvocationOverview, LoadingSpan };
    }
});
